'use client'
import { cubicBezier } from 'motion'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { Content } from '@/content-types'
import { isFilled } from '@/lib/content-fields'
import { RichText, type SliceComponentProps } from '@/lib/rich-text'
/**
 * Props for `IntegrationSlice`.
 */
export type IntegrationSliceProps = SliceComponentProps<Content.IntegrationSliceSlice>

/**
 * Component for "IntegrationSlice" Slices.
 */
const IntegrationSlice: FC<IntegrationSliceProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="container flex flex-col items-center justify-center gap-3 pt-[77px] pb-12 lg:pb-28"
    >
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{
          ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
          duration: 1.25,
        }}
      >
        <RichText
          field={slice.primary.title}
          components={{
            heading1: ({ children }) => (
              <h1
                className={`font-rubik text-foreground w-full text-center text-2xl font-medium tracking-tight lg:text-5xl`}
              >
                {children}
              </h1>
            ),
          }}
        />
        <RichText
          field={slice.primary.text}
          components={{
            paragraph: ({ children }) => (
              <p className="text-foreground text-center text-base leading-[33px] font-light tracking-tight opacity-90 lg:text-[22px]">
                {children}
              </p>
            ),
            strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          }}
        />
        <div className="mt-10 grid grid-cols-2 gap-y-[35px] md:mt-20 md:grid-cols-4 lg:gap-x-24">
          {slice.primary.integrated_app.map((item) => (
            <div key={item.title} className="flex flex-col">
              <div className="flex items-center gap-2">
                {isFilled.select(item.icon) && (
                  <IconComponent icon={item.icon} size="large" className="text-secondary !size-10 shrink-0" />
                )}

                <p className="font-rubik text-foreground text-lg leading-7 font-light tracking-tighter">
                  {item.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  )
}

export default IntegrationSlice
